import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBeerMugEmpty,
  faBlender,
  faBottleWater,
  faBox,
  faCanFood,
  faCupStrawSwoosh,
  faCupTogo,
  faGlassCitrus,
  faGlassWater,
  faGlassWaterDroplet,
  faJar,
  faJugBottle,
  faMugHot,
  faMugSaucer,
  faMugTeaSaucer,
  faWhiskeyGlass,
  faWineGlass,
} from '@fortawesome/pro-regular-svg-icons';

/**
 * Resolves a hydration type's `icon` column to real artwork.
 *
 * The column holds a Font Awesome icon name in kebab case ("glass-water"),
 * chosen by whoever inserts the row. A lookup is still needed because Font
 * Awesome icons must be imported to be bundled — there is no way to fetch one
 * by name at runtime — so this whitelist is what decides which icons ship.
 *
 * Adding a type with an icon that isn't here renders the fallback rather than
 * breaking: the table is edited by hand and can name an icon before the apps
 * ship support for it. Widening the whitelist is a one-line change.
 *
 * Shared rather than per-app so client-app and coach-portal cannot drift into
 * drawing different glyphs for the same type.
 */
const ICONS_BY_NAME: Record<string, IconDefinition> = {
  'glass-water': faGlassWater,
  'glass-water-droplet': faGlassWaterDroplet,
  'glass-citrus': faGlassCitrus,
  'cup-straw-swoosh': faCupStrawSwoosh,
  'bottle-water': faBottleWater,
  'jug-bottle': faJugBottle,
  'mug-saucer': faMugSaucer,
  'mug-tea-saucer': faMugTeaSaucer,
  'mug-hot': faMugHot,
  'can-food': faCanFood,
  'beer-mug-empty': faBeerMugEmpty,
  'whiskey-glass': faWhiskeyGlass,
  'wine-glass': faWineGlass,
  blender: faBlender,
  jar: faJar,
  box: faBox,
  'cup-togo': faCupTogo,
};

export const HYDRATION_TYPE_FALLBACK_ICON = faGlassWater;

/**
 * The glyph for each package kind (HydrationPackageKind on the wire), drawn on
 * the amount chips and the wheel's size markers. Derived here rather than sent
 * by the API so the kind stays a closed enum and the icon stays bundled. Font
 * Awesome has no carton, so a tetra pack is a box.
 */
export const HYDRATION_PACKAGE_KIND_ICONS: Record<string, IconDefinition> = {
  Can: faCanFood,
  Bottle: faBottleWater,
  Pack: faBox,
  Cup: faCupTogo,
};

/**
 * Every icon the maps can yield — components pass this to the FA icon library.
 * The package-kind icons are all in the name map too, so registering this list
 * covers the chips and markers as well as the tiles.
 */
export const HYDRATION_TYPE_ICONS: IconDefinition[] = [
  ...new Set([...Object.values(ICONS_BY_NAME), ...Object.values(HYDRATION_PACKAGE_KIND_ICONS)]),
  HYDRATION_TYPE_FALLBACK_ICON,
];

/** The icon names this build can draw — useful when writing the seed SQL. */
export const SUPPORTED_HYDRATION_ICON_NAMES = Object.keys(ICONS_BY_NAME);

export function hydrationTypeIcon(icon: string | undefined): IconDefinition {
  if (!icon) return HYDRATION_TYPE_FALLBACK_ICON;
  return ICONS_BY_NAME[icon.trim().toLowerCase()] ?? HYDRATION_TYPE_FALLBACK_ICON;
}

/** A package kind's glyph; an unknown or missing kind draws the fallback glass. */
export function hydrationPackageKindIcon(kind: string | null | undefined): IconDefinition {
  if (!kind) return HYDRATION_TYPE_FALLBACK_ICON;
  return HYDRATION_PACKAGE_KIND_ICONS[kind] ?? HYDRATION_TYPE_FALLBACK_ICON;
}
